import React, { useState }    from 'react';
import { useNavigate }         from 'react-router-dom';
import {
  Card, Button, Upload, Alert,
  Table, Tag, Steps, Row, Col, message, Spin,
  Tooltip,
} from 'antd';
import {
  UploadOutlined, ArrowLeftOutlined,
  CheckCircleOutlined, FileExcelOutlined,
  WarningOutlined, DownloadOutlined, EyeOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import api from '../../services/api';
import employeeService from '../../services/employeeService';

const { Dragger } = Upload;

// All columns including new cohort + resigned fields
// ── Column definitions ──────────────────────────────────────────
// aagmanStatus values: Attended | Pending | Backout | Deferment | NA
//   Backout   = employee decided NOT to join
//   Deferment = employee requesting to join later / next cohort
// aagmanReason = required when status is Backout or Deferment
// Manager Follow-up fields are optional but recommended

// ── Aagman = Training Programme (separate from joining status) ──
const AAGMAN_STATUSES = ['Attended','Pending','NA'];
const AAGMAN_STATUS_INFO = {
  Attended: { color:'#16a34a', bg:'#f0fdf4', desc:'Employee attended the Aagman programme' },
  Pending:  { color:'#b45309', bg:'#fffbeb', desc:'Aagman attendance not yet confirmed'    },
  NA:       { color:'#7a96b0', bg:'#f5f8fc', desc:'Not applicable'                         },
};

// ── Cohort Status = Employee joining status in the organisation ──
const COHORT_STATUSES = ['Enrolled','Attended','Deferment','Backout'];
const COHORT_STATUS_INFO = {
  Enrolled:  { color:'#1E6FBF', bg:'#EAF1F8', desc:'Invited/expected to join — default'      },
  Attended:  { color:'#16a34a', bg:'#f0fdf4', desc:'Confirmed joined the organisation'       },
  Deferment: { color:'#7c3aed', bg:'#f5f3ff', desc:'Postponed — will join next cohort'       },
  Backout:   { color:'#dc2626', bg:'#fef2f2', desc:'Decided NOT to join — seat lost'         },
};

const TEMPLATE_COLUMNS = [
  // ── Employee Identity ──────────────────────────────────────
  'empCode', 'empName', 'designation', 'division', 'hq',
  'mobile', 'empEmail', 'dateOfJoining',
  // ── Aagman / Joining Status ────────────────────────────────
  // ── Cohort joining status (separate from Aagman) ────────────
  'cohortStatus',       // Enrolled | Attended | Deferment | Backout
  'cohortStatusReason', // required when Deferment or Backout
  // ── Aagman programme (training log only) ─────────────────────
  'aagmanStatus',       // Attended | Pending | NA
  'aagmanDate',         // required when aagmanStatus = Attended
  // ── Cohort ────────────────────────────────────────────────
  'cohortName', 'cohortDate',
  // ── Reporting Manager ─────────────────────────────────────
  'rmCode', 'rmName', 'managerEmail', 'managerMobile', 'callDate',
  // ── Manager Follow-up (Day -1 Onboarding) ─────────────────
  'accompanied',       // Did manager accompany BE during Pratham? Yes/No
  'ipadProvided',      // Was iPad/laptop provided? Yes/No
  'ipadStatus',        // Active | Pending | Not Issued
  'prathamDate',       // Date of Pratham
  'prathamCandidAtten',// Candidate attendance: Present | Absent
  'prathamMgrAtten',   // Manager attendance:   Present | Absent
  'mgrRemarks',        // Any notes from manager
];

const REQUIRED_COLUMNS = ['empName','division','hq','dateOfJoining','rmCode','rmName'];
// aagmanReason required when Backout or Deferment
// aagmanDate required when Attended

export default function BulkUpload() {
  const navigate = useNavigate();

  const [step,        setStep]       = useState(0);
  const [file,        setFile]       = useState(null);
  const [loading,     setLoading]    = useState(false);
  const [parsing,     setParsing]    = useState(false);
  const [previewRows, setPreviewRows]= useState([]);   // parsed rows from Excel
  const [result,      setResult]     = useState(null);
  const [error,       setError]      = useState('');

  // ── Parse Excel file at frontend for preview ─────────────────
  const parseFile = (f) => {
    setParsing(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'array', cellDates: true });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: '', raw: false });
        // raw:false tells SheetJS to format everything as strings,
        // which avoids hyperlink/richText cells becoming "[object Object]"

        // Normalise date fields to YYYY-MM-DD string
        const DATE_FIELDS = ['dateOfJoining', 'callDate', 'cohortDate', 'aagmanDate'];

        // Safe text extractor — handles hyperlink/richText objects from xlsx
        function cellStr(v) {
          if (v == null) return '';
          if (typeof v === 'string') return v.trim();
          if (typeof v === 'object' && v.text  != null) return String(v.text).trim();
          if (typeof v === 'object' && v.result != null) return String(v.result).trim();
          if (Array.isArray(v?.richText)) return v.richText.map(r => r.text || '').join('').trim();
          return String(v).trim();
        }

        const normalised = rows.map((row, idx) => {
          // Sanitise every cell value first
          const r = { _rowNum: idx + 2 };
          Object.keys(row).forEach(k => { r[k] = cellStr(row[k]); });
          DATE_FIELDS.forEach(f => {
            if (r[f] instanceof Date) {
              r[f] = r[f].toISOString().split('T')[0];
            }
          });
          // Validate required fields
          const missing = REQUIRED_COLUMNS.filter(c => !r[c]);
          const errors = missing.map(c => `${c} is required`);
          // Cohort status validation
          if (r.cohortStatus && !COHORT_STATUSES.includes(r.cohortStatus)) {
            errors.push(`Invalid cohortStatus "${r.cohortStatus}". Must be: ${COHORT_STATUSES.join(' | ')}`);
          }
          if ((r.cohortStatus === 'Deferment' || r.cohortStatus === 'Backout') && !r.cohortStatusReason) {
            errors.push(`cohortStatusReason required when cohortStatus = ${r.cohortStatus}`);
          }
          // Aagman validation (programme attendance — separate)
          if (r.aagmanStatus && !AAGMAN_STATUSES.includes(r.aagmanStatus)) {
            errors.push(`Invalid aagmanStatus "${r.aagmanStatus}". Must be: ${AAGMAN_STATUSES.join(' | ')}`);
          }
          if (r.aagmanStatus === 'Attended' && !r.aagmanDate) {
            errors.push('aagmanDate required when aagmanStatus = Attended');
          }
          r._errors = errors;
          return r;
        });

        setPreviewRows(normalised);
        setStep(1);
      } catch {
        message.error('Failed to read file — ensure it is a valid Excel file');
      } finally {
        setParsing(false);
      }
    };
    reader.readAsArrayBuffer(f);
  };

  // ── Import to backend — direct FormData POST ─────────────────
  const handleUpload = async () => {
    if (!file) { message.warning('Please select a file first'); return; }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Use api instance — baseURL and auth token handled automatically
      let data;
      try {
        const res = await api.post('/employees/bulk-import', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        data = res.data ?? res;
      } catch (fetchErr) {
        setError(fetchErr?.response?.data?.message || fetchErr.message || 'Upload failed');
        setStep(2);
        return;
      }

      // ✅ Success — API wraps payload as { isError, message, data: {...}, code }
      const payload = data?.data ?? data;   // unwrap if nested
      setResult(payload);
      setStep(2);
      const imported = payload?.imported ?? 0;
      const failed   = payload?.failed   ?? 0;
      message.success(
        `Import complete — ${imported} imported${failed > 0 ? `, ${failed} failed` : ''}`
      );

    } catch (err) {
      // Only a genuine network error (no response at all) lands here
      const raw = (err.message || '').toLowerCase();
      const isNetwork = raw.includes('failed to fetch') ||
                        raw.includes('networkerror') ||
                        raw.includes('load failed') ||
                        raw.includes('network request failed');
      setError(isNetwork ? 'NETWORK_ERROR' : (err.message || 'Import failed'));
      setStep(2);
    } finally {
      setLoading(false);
    }
  };

  // ── Download error Excel returned from backend ────────────────
  function downloadErrorFile(base64, filename = 'Import_Errors.xlsx') {
    const binary = atob(base64);
    const bytes  = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  }

  // ── Download .xlsx template ───────────────────────────────────
  const downloadTemplate = () => {
    // 3 sample rows showing all possible aagmanStatus values
    const sampleRows = [
      {
        // ── Employee 1: Attended (joined & present at Pratham) ──
        empCode:'L04480', empName:'John Smith', designation:'Medical Rep',
        division:'Nikkos', hq:'Mumbai', mobile:'9876543210',
        empEmail:'john@erislifesciences.com', dateOfJoining:'2026-03-01',
        cohortStatus:'Attended', cohortStatusReason:'',
        aagmanStatus:'Attended', aagmanDate:'2026-03-01',
        cohortName:'March 2026 Batch', cohortDate:'2026-03-01',
        rmCode:'M01234', rmName:'Raj Kumar',
        managerEmail:'raj@erislifesciences.com', managerMobile:'9876543211',
        callDate:'2026-02-28',
        // Manager follow-up
        accompanied:'Yes', ipadProvided:'Yes', ipadStatus:'Active',
        prathamDate:'2026-03-01', prathamCandidAtten:'Present',
        prathamMgrAtten:'Present', mgrRemarks:'Smooth onboarding',
      },
      {
        // ── Employee 2: Backout (decided NOT to join) ──
        empCode:'L04481', empName:'Priya Sharma', designation:'Medical Rep',
        division:'Aspire', hq:'Delhi', mobile:'9876543211',
        empEmail:'priya@erislifesciences.com', dateOfJoining:'2026-03-01',
        cohortStatus:'Backout', cohortStatusReason:'Accepted competitor offer',
        aagmanStatus:'Pending', aagmanDate:'',
        cohortName:'March 2026 Batch', cohortDate:'2026-03-01',
        rmCode:'M01235', rmName:'Anita Verma',
        managerEmail:'anita@erislifesciences.com', managerMobile:'9876543212',
        callDate:'', accompanied:'', ipadProvided:'', ipadStatus:'',
        prathamDate:'', prathamCandidAtten:'', prathamMgrAtten:'', mgrRemarks:'',
      },
      {
        // ── Employee 3: Deferment (joining postponed to next cohort) ──
        empCode:'L04482', empName:'Amit Patel', designation:'Medical Rep',
        division:'Rise', hq:'Ahmedabad', mobile:'9876543213',
        empEmail:'amit@erislifesciences.com', dateOfJoining:'2026-04-01',
        cohortStatus:'Deferment', cohortStatusReason:'Family emergency — joining April batch',
        aagmanStatus:'Pending', aagmanDate:'',
        cohortName:'March 2026 Batch', cohortDate:'2026-03-01',
        rmCode:'M01236', rmName:'Suresh Patil',
        managerEmail:'suresh@erislifesciences.com', managerMobile:'9876543214',
        callDate:'', accompanied:'', ipadProvided:'', ipadStatus:'',
        prathamDate:'', prathamCandidAtten:'', prathamMgrAtten:'', mgrRemarks:'',
      },
    ];

    const ws = XLSX.utils.json_to_sheet(sampleRows);

    // Style header row width
    ws['!cols'] = TEMPLATE_COLUMNS.map(() => ({ wch: 20 }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Employees');
    XLSX.writeFile(wb, 'EJT_Employee_Template.xlsx');
    message.success('Template downloaded!');
  };

  // ── Preview table columns ────────────────────────────────────
  const previewColumns = [
    { title:'#',          dataIndex:'_rowNum',      width:55,
      render: v => <span style={{color:'#7a96b0',fontSize:12}}>{v}</span> },
    { title:'Emp Code',   dataIndex:'empCode',       width:100,
      render: v => v
        ? <span style={{fontFamily:'monospace',fontWeight:600,color:'#1B2D6B'}}>{v}</span>
        : <span style={{color:'#c0cfe0',fontSize:11,fontStyle:'italic'}}>null — allowed</span> },
    { title:'Name',       dataIndex:'empName',       width:160,
      render: v => <span style={{fontWeight:500}}>{v||'—'}</span> },
    { title:'Designation',dataIndex:'designation',   width:130 },
    { title:'Division',   dataIndex:'division',      width:110 },
    { title:'HQ',         dataIndex:'hq',            width:100 },
    { title:'DOJ',        dataIndex:'dateOfJoining', width:110 },
    { title:'Joining Status', dataIndex:'aagmanStatus', width:110,
      render: v => {
        const cfg = AAGMAN_STATUS_INFO[v] || { color:'#7a96b0', bg:'#f5f8fc' };
        return v
          ? <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11,
              fontWeight:700, color:cfg.color, background:cfg.bg,
              border:`1px solid ${cfg.color}44` }}>{v}</span>
          : <span style={{ color:'#c0cfe0' }}>—</span>;
      }},
    { title:'Attend Date', dataIndex:'aagmanDate', width:110,
      render: (v, r) => r.aagmanStatus === 'Attended'
        ? <span style={{color:'#15803d',fontWeight:600}}>{v||'⚠ required'}</span>
        : <span style={{color:'#c0cfe0'}}>—</span>
    },
    { title:'Cohort',      dataIndex:'cohortName',        width:130 },
    { title:'Cohort Date', dataIndex:'cohortDate',        width:110 },
    { title:'Cohort Status', dataIndex:'cohortStatus',    width:120,
      render: v => {
        const cfg = COHORT_STATUS_INFO[v] || { color:'#7a96b0', bg:'#f5f8fc' };
        return v
          ? <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11,
              fontWeight:700, color:cfg.color, background:cfg.bg,
              border:`1px solid ${cfg.color}44`}}>{v}</span>
          : <span style={{ padding:'2px 8px', borderRadius:20, fontSize:11,
              fontWeight:700, color:'#1E6FBF', background:'#EAF1F8',
              border:'1px solid #1E6FBF44'}}>Enrolled</span>;
      }},
    { title:'Reason', dataIndex:'cohortStatusReason', width:160, ellipsis:true,
      render:(v,r) => {
        if(r.cohortStatus==='Deferment'||r.cohortStatus==='Backout') {
          return v
            ?<Tooltip title={v}><span style={{fontSize:11,color:'#4a6580'}}>{v}</span></Tooltip>
            :<span style={{color:'#dc2626',fontSize:11,fontWeight:700}}>⚠ required</span>;
        }
        return <span style={{color:'#c0cfe0'}}>—</span>;
      }},
    { title:'RM Name',    dataIndex:'rmName',        width:130 },
    { title:'Manager Mobile',dataIndex:'managerMobile',width:120 },

    { title:'Mgr Follow-up', width:120, align:'center',
      render:(_,r)=>{
        const has = r.accompanied||r.ipadProvided||r.prathamDate;
        return has
          ?<Tag color="blue" style={{fontSize:10,borderRadius:20}}>✓ Filled</Tag>
          :<span style={{color:'#c0cfe0',fontSize:11}}>—</span>;
      }},
    { title:'Status', dataIndex:'_errors', width:140, fixed:'right',
      render: (errs, r) => errs?.length
        ? <button
            onClick={e => { e.stopPropagation(); setExpandedErrorRow(expandedErrorRow === r._rowNum ? null : r._rowNum); }}
            style={{
              background:'#fef2f2', border:'1px solid #fca5a5',
              borderRadius:20, padding:'3px 10px',
              color:'#dc2626', fontWeight:700, fontSize:11,
              cursor:'pointer', display:'flex', alignItems:'center', gap:5,
            }}>
            ⚠ {errs.length} error{errs.length>1?'s':''}
            <span style={{fontSize:10, opacity:.7}}>
              {expandedErrorRow === r._rowNum ? '▲' : '▼'}
            </span>
          </button>
        : <span style={{
            background:'#f0fdf4', border:'1px solid #86efac',
            borderRadius:20, padding:'3px 10px',
            color:'#16a34a', fontWeight:700, fontSize:11,
          }}>✓ OK</span>
    },
  ];

  const errorColumns = [
    { title:'Row',      dataIndex:'row',     width:55,
      render:v=><span style={{color:'#7a96b0',fontSize:12,fontWeight:600}}>{v}</span> },
    { title:'Emp Code', dataIndex:'empCode', width:110,
      render:v=>v
        ?<span style={{fontFamily:'monospace',fontWeight:700,color:'#1B2D6B'}}>{v}</span>
        :<span style={{color:'#c0cfe0',fontSize:11,fontStyle:'italic'}}>null</span> },
    { title:'Name',     dataIndex:'empName', width:180,
      render:v=><span style={{fontWeight:500}}>{v||'—'}</span> },
    { title:'Error Details', dataIndex:'errors',
      render:errs=>(
        <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
          {(Array.isArray(errs)?errs:[errs]).map((e,i)=>(
            <Tag key={i} color="error"
              style={{marginBottom:2,whiteSpace:'normal',lineHeight:1.4,maxWidth:400}}>
              {e}
            </Tag>
          ))}
        </div>
      )},
  ];

  const [showErrors,       setShowErrors]       = React.useState(false);
  const [expandedErrorRow, setExpandedErrorRow] = React.useState(null);
  const [filterMode,       setFilterMode]       = React.useState('all'); // 'all'|'errors'|'valid'

  const validCount   = previewRows.filter(r => !r._errors?.length).length;
  const invalidCount = previewRows.filter(r =>  r._errors?.length).length;

  const displayRows = filterMode === 'errors'
    ? previewRows.filter(r => r._errors?.length)
    : filterMode === 'valid'
      ? previewRows.filter(r => !r._errors?.length)
      : previewRows;

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/employees')} />
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#1a2d4a', margin: 0 }}>
            Bulk Upload
          </h2>
          <p style={{ fontSize: 13, color: '#7a96b0', margin: 0 }}>
            Import multiple employees via Excel / CSV
          </p>
        </div>
      </div>

      {/* ── Steps ── */}
      <Steps
        current={step}
        style={{ marginBottom: 28 }}
        items={[
          { title: 'Select File',   description: 'Choose Excel file' },
          { title: 'Preview Data',  description: 'Review before import' },
          { title: 'Done',          description: 'Import summary' },
        ]}
      />

      {/* ── Step 0: Upload ── */}
      {step === 0 && (
        <Row gutter={[20, 20]}>
          <Col xs={24} md={16}>
            <Card
              title={<span style={{ fontWeight: 600 }}>Upload Employee File</span>}
              style={{ borderRadius: 12 }}
              bodyStyle={{ padding: '20px 24px' }}
            >
              <Dragger
                accept=".xlsx,.xls"
                beforeUpload={(f) => {
                  setFile(f);
                  parseFile(f);   // ← parse immediately for preview
                  return false;
                }}
                maxCount={1}
                style={{ borderRadius: 10 }}
                showUploadList={false}
              >
                {parsing ? (
                  <div style={{padding:'40px 0'}}>
                    <Spin size="large" tip="Reading file..." />
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: 36, color: '#1E6FBF', marginBottom: 8 }}>
                      <FileExcelOutlined />
                    </p>
                    <p style={{ fontSize: 15, fontWeight: 600, color: '#1a2d4a' }}>
                      Click or drag Excel file here
                    </p>
                    <p style={{ fontSize: 13, color: '#7a96b0' }}>
                      Supports .xlsx, .xls · Max 10MB
                    </p>
                  </>
                )}
              </Dragger>

              <div style={{ display: 'flex', gap: 12, marginTop: 20, justifyContent: 'flex-end' }}>
                <Button icon={<DownloadOutlined />} onClick={downloadTemplate}>
                  Download Template (.xlsx)
                </Button>
              </div>
            </Card>
          </Col>

          <Col xs={24} md={10}>
            <div style={{ display:'flex', flexDirection:'column', gap:12 }}>

              {/* Cohort Status guide */}
              <div style={{ background:'#fff', border:'1px solid #dce6f0',
                borderRadius:12, overflow:'hidden' }}>
                <div style={{ padding:'10px 16px', fontWeight:700, fontSize:13,
                  color:'#fff', background:'#1B2D6B' }}>
                  🏢 Cohort Status — Employee Joining
                </div>
                <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {Object.entries(COHORT_STATUS_INFO).map(([key, cfg]) => (
                    <div key={key} style={{ display:'flex', alignItems:'flex-start', gap:10,
                      padding:'8px 12px', borderRadius:8,
                      background:cfg.bg, border:`1px solid ${cfg.color}33` }}>
                      <span style={{ fontWeight:800, fontSize:12, color:cfg.color,
                        minWidth:85, fontFamily:'monospace' }}>{key}</span>
                      <span style={{ fontSize:11, color:'#4a6580', lineHeight:1.4 }}>
                        {cfg.desc}
                      </span>
                    </div>
                  ))}
                  <div style={{ fontSize:11, color:'#7c3aed', padding:'6px 10px',
                    background:'#f5f3ff', borderRadius:8, border:'1px solid #c4b5fd' }}>
                    ⚠️ <strong>Deferment / Backout</strong> require <code>cohortStatusReason</code>
                  </div>
                </div>
              </div>

              {/* Aagman Status guide */}
              <div style={{ background:'#fff', border:'1px solid #dce6f0',
                borderRadius:12, overflow:'hidden' }}>
                <div style={{ padding:'10px 16px', fontWeight:700, fontSize:13,
                  color:'#fff', background:'#1E6FBF' }}>
                  🎓 Aagman Status — Programme Attendance
                </div>
                <div style={{ padding:'12px 16px', display:'flex', flexDirection:'column', gap:8 }}>
                  {Object.entries(AAGMAN_STATUS_INFO).map(([key, cfg]) => (
                    <div key={key} style={{ display:'flex', alignItems:'flex-start', gap:10,
                      padding:'8px 12px', borderRadius:8,
                      background:cfg.bg, border:`1px solid ${cfg.color}33` }}>
                      <span style={{ fontWeight:800, fontSize:12, color:cfg.color,
                        minWidth:78, fontFamily:'monospace' }}>{key}</span>
                      <span style={{ fontSize:11, color:'#4a6580', lineHeight:1.4 }}>
                        {cfg.desc}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Manager Follow-up guide */}
              <div style={{ background:'#fff', border:'1px solid #dce6f0', borderRadius:12, overflow:'hidden' }}>
                <div style={{ padding:'10px 16px', fontWeight:700, fontSize:13,
                  color:'#fff', background:'#1E6FBF' }}>
                  📋 Manager Follow-up Fields (optional)
                </div>
                <div style={{ padding:'12px 16px' }}>
                  {[
                    ['accompanied',        'Yes / No',             'Did manager accompany during Pratham?'],
                    ['ipadProvided',       'Yes / No',             'Was iPad/laptop provided?'],
                    ['ipadStatus',         'Active/Pending/Not Issued','Current device status'],
                    ['prathamDate',        'YYYY-MM-DD',           'Date of Pratham programme'],
                    ['prathamCandidAtten', 'Present / Absent',     'Candidate at Pratham?'],
                    ['prathamMgrAtten',    'Present / Absent',     'Manager at Pratham?'],
                    ['mgrRemarks',         'Free text',            'Manager observations'],
                  ].map(([col, val, desc]) => (
                    <div key={col} style={{ display:'flex', gap:8, marginBottom:7,
                      fontSize:11, alignItems:'flex-start' }}>
                      <code style={{ background:'#eff6ff', color:'#1d4ed8',
                        padding:'1px 5px', borderRadius:4, fontWeight:700,
                        flexShrink:0, fontSize:11 }}>{col}</code>
                      <span style={{ color:'#7a96b0' }}>{val}</span>
                      <span style={{ color:'#4a6580', marginLeft:'auto' }}>— {desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Rules */}
              <div style={{ padding:'10px 14px', background:'#fff8f0',
                borderRadius:8, border:'1px solid #ffe0b2', fontSize:11, color:'#b45309' }}>
                <WarningOutlined style={{ marginRight:6 }}/>
                Dates must be <strong>YYYY-MM-DD</strong> · <strong>empCode</strong> is optional — rows without it are still imported · Duplicate empCode rows are skipped
              </div>
            </div>
          </Col>
        </Row>
      )}

      {/* ── Step 1: Preview parsed data ── */}
      {step === 1 && (
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>

          {/* ── Summary bar ──────────────────────────────────── */}
          <div style={{
            background:'#fff', borderRadius:12, padding:'16px 20px',
            border:'1px solid #dce6f0',
            display:'flex', alignItems:'center', justifyContent:'space-between',
            flexWrap:'wrap', gap:12,
          }}>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <FileExcelOutlined style={{ fontSize:28, color:'#15803d' }} />
              <div>
                <div style={{ fontWeight:700, fontSize:14, color:'#1a2d4a' }}>{file?.name}</div>
                <div style={{ fontSize:12, color:'#7a96b0' }}>{previewRows.length} rows read from file</div>
              </div>
            </div>
            {/* Summary chips + filter toggle */}
            <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
              {[
                { key:'all',    label:`All ${previewRows.length}`,        bg:'#eff6ff', color:'#1d4ed8', border:'#bfdbfe' },
                { key:'valid',  label:`✓ ${validCount} Valid`,            bg:'#f0fdf4', color:'#15803d', border:'#86efac' },
                { key:'errors', label:`✗ ${invalidCount} With Errors`,    bg:'#fef2f2', color:'#b91c1c', border:'#fca5a5' },
              ].map(f => (
                <button key={f.key} onClick={() => setFilterMode(f.key)} style={{
                  padding:'5px 14px', borderRadius:20, fontWeight:700,
                  fontSize:12, cursor:'pointer',
                  background: filterMode===f.key ? f.color : f.bg,
                  color:       filterMode===f.key ? '#fff'   : f.color,
                  border:     `1px solid ${f.border}`,
                }}>
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Error summary panel (shown when errors exist) ── */}
          {invalidCount > 0 && (
            <div style={{
              background:'#fff', borderRadius:12,
              border:'1.5px solid #fca5a5', overflow:'hidden',
            }}>
              {/* Header */}
              <div
                onClick={() => setShowErrors(v => !v)}
                style={{
                  padding:'12px 18px', cursor:'pointer',
                  background: showErrors ? '#fef2f2' : '#fff',
                  display:'flex', alignItems:'center', justifyContent:'space-between',
                }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{
                    width:30, height:30, borderRadius:8,
                    background:'#fee2e2', display:'flex',
                    alignItems:'center', justifyContent:'center',
                    fontSize:16, flexShrink:0,
                  }}>⚠️</span>
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color:'#b91c1c' }}>
                      {invalidCount} row{invalidCount>1?'s':''} have validation errors
                    </div>
                    <div style={{ fontSize:11, color:'#7a96b0', marginTop:1 }}>
                      These rows will be <strong>skipped</strong> during import.
                      Fix them in Excel and re-upload, or import only the {validCount} valid rows now.
                    </div>
                  </div>
                </div>
                <div style={{
                  padding:'4px 12px', borderRadius:8,
                  background:'#fee2e2', color:'#dc2626',
                  fontSize:12, fontWeight:700, cursor:'pointer', flexShrink:0,
                }}>
                  {showErrors ? '▲ Hide Errors' : '▼ Show All Errors'}
                </div>
              </div>

              {/* Expanded error list */}
              {showErrors && (
                <div style={{ borderTop:'1px solid #fecaca' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ background:'#fff5f5' }}>
                        {['Row','Emp Code','Name','Error(s)'].map(h => (
                          <th key={h} style={{
                            padding:'8px 14px', textAlign:'left',
                            fontSize:11, fontWeight:700, color:'#7a96b0',
                            textTransform:'uppercase', letterSpacing:'0.5px',
                            borderBottom:'1px solid #fecaca',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.filter(r => r._errors?.length).map((r, i) => (
                        <tr key={r._rowNum} style={{
                          borderBottom:'1px solid #fee2e2',
                          background: i%2===0 ? '#fff' : '#fff5f5',
                        }}>
                          <td style={{ padding:'8px 14px', fontWeight:700,
                            color:'#7a96b0', fontSize:12 }}>
                            #{r._rowNum}
                          </td>
                          <td style={{ padding:'8px 14px' }}>
                            {r.empCode
                              ? <span style={{ fontFamily:'monospace', fontWeight:700,
                                  color:'#1B2D6B', fontSize:12 }}>{r.empCode}</span>
                              : <span style={{ color:'#c0cfe0', fontSize:11,
                                  fontStyle:'italic' }}>null</span>}
                          </td>
                          <td style={{ padding:'8px 14px', fontSize:13,
                            fontWeight:600, color:'#1a2d4a' }}>
                            {r.empName || '—'}
                          </td>
                          <td style={{ padding:'8px 14px' }}>
                            <div style={{ display:'flex', flexWrap:'wrap', gap:4 }}>
                              {(r._errors||[]).map((e, ei) => (
                                <span key={ei} style={{
                                  display:'inline-flex', alignItems:'center', gap:5,
                                  background:'#fef2f2', border:'1px solid #fca5a5',
                                  borderRadius:6, padding:'3px 10px',
                                  fontSize:11, color:'#b91c1c', fontWeight:600,
                                }}>
                                  ✗ {e}
                                </span>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── All valid banner ──────────────────────────────── */}
          {invalidCount === 0 && (
            <div style={{
              background:'#f0fdf4', borderRadius:12, padding:'12px 18px',
              border:'1px solid #86efac',
              display:'flex', alignItems:'center', gap:10,
              fontSize:13, fontWeight:600, color:'#15803d',
            }}>
              🎉 All {previewRows.length} rows are valid and ready to import!
            </div>
          )}

          {/* ── Preview table ─────────────────────────────────── */}
          <div style={{
            background:'#fff', borderRadius:12,
            border:'1px solid #dce6f0', overflow:'hidden',
          }}>
            <div style={{
              padding:'12px 16px', borderBottom:'1px solid #dce6f0',
              display:'flex', alignItems:'center', justifyContent:'space-between',
            }}>
              <span style={{ fontWeight:700, fontSize:13, color:'#1a2d4a',
                display:'flex', alignItems:'center', gap:8 }}>
                <EyeOutlined style={{ color:'#1E6FBF' }} />
                Data Preview
                <span style={{ fontSize:11, fontWeight:400, color:'#7a96b0' }}>
                  — {displayRows.length} row{displayRows.length!==1?'s':''} shown
                  {filterMode !== 'all' && ` (filtered: ${filterMode})`}
                </span>
              </span>
              <span style={{ fontSize:11, color:'#7a96b0' }}>
                Click <strong>⚠ error</strong> badge in Status column to see details inline
              </span>
            </div>
            <Table
              dataSource={displayRows}
              columns={previewColumns}
              rowKey="_rowNum"
              size="small"
              scroll={{ x: 1500 }}
              pagination={{
                pageSize: 15, showSizeChanger: true,
                pageSizeOptions: [15,30,50,100],
                showTotal: t => `${t} rows`,
              }}
              rowClassName={r => r._errors?.length ? 'bulk-row-error' : ''}
              expandable={{
                expandedRowKeys: expandedErrorRow ? [expandedErrorRow] : [],
                showExpandColumn: false,
                expandedRowRender: r => (
                  <div style={{
                    padding:'12px 20px', background:'#fff5f5',
                    borderTop:'1px solid #fecaca',
                  }}>
                    <div style={{
                      fontSize:12, fontWeight:700, color:'#b91c1c',
                      marginBottom:8, display:'flex', alignItems:'center', gap:6,
                    }}>
                      ⚠️ Row #{r._rowNum} — {r._errors?.length} error{r._errors?.length>1?'s':''}
                      <span style={{ fontWeight:400, color:'#7a96b0' }}>
                        ({r.empName})
                      </span>
                    </div>
                    <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                      {(r._errors||[]).map((e, i) => (
                        <div key={i} style={{
                          display:'flex', alignItems:'flex-start', gap:6,
                          background:'#fef2f2', border:'1px solid #fca5a5',
                          borderRadius:8, padding:'6px 12px',
                          fontSize:12, color:'#b91c1c',
                        }}>
                          <span style={{ fontWeight:800, flexShrink:0 }}>{i+1}.</span>
                          <span>{e}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{
                      marginTop:10, fontSize:11, color:'#7a96b0',
                      padding:'6px 10px', background:'#fffbeb',
                      borderRadius:6, border:'1px solid #fde68a',
                    }}>
                      💡 Fix this row in your Excel file and re-upload, or continue — this row will be skipped.
                    </div>
                  </div>
                ),
                onExpand: (expanded, r) =>
                  setExpandedErrorRow(expanded ? r._rowNum : null),
              }}
            />
          </div>

          {/* ── Action footer ─────────────────────────────────── */}
          <div style={{
            background:'#fff', borderRadius:12, padding:'16px 20px',
            border:'1px solid #dce6f0',
            display:'flex', justifyContent:'space-between',
            alignItems:'center', flexWrap:'wrap', gap:12,
          }}>
            <div>
              {validCount > 0 ? (
                <div style={{ fontSize:13, color:'#1a2d4a', fontWeight:600 }}>
                  Ready to import{' '}
                  <span style={{
                    color:'#15803d', background:'#f0fdf4',
                    padding:'1px 8px', borderRadius:20,
                    border:'1px solid #86efac',
                  }}>
                    {validCount} valid row{validCount!==1?'s':''}
                  </span>
                  {invalidCount > 0 && (
                    <span style={{ marginLeft:8, fontSize:12, color:'#b91c1c' }}>
                      · {invalidCount} row{invalidCount!==1?'s':''} with errors will be skipped
                    </span>
                  )}
                </div>
              ) : (
                <div style={{
                  fontSize:13, color:'#b91c1c', fontWeight:600,
                  display:'flex', alignItems:'center', gap:6,
                }}>
                  ❌ No valid rows to import — fix errors in Excel and re-upload
                </div>
              )}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Button onClick={() => {
                setFile(null); setPreviewRows([]);
                setStep(0); setShowErrors(false);
                setFilterMode('all'); setExpandedErrorRow(null);
              }}>
                ← Choose Different File
              </Button>
              <Button
                type="primary"
                icon={<UploadOutlined />}
                loading={loading}
                disabled={validCount === 0}
                onClick={handleUpload}
                style={{
                  background: validCount > 0 ? '#1B2D6B' : undefined,
                  minWidth: 180, fontWeight: 700, fontSize: 13,
                }}
              >
                {loading
                  ? 'Importing…'
                  : validCount === 0
                    ? 'No valid rows'
                    : `🚀 Import ${validCount} Employee${validCount!==1?'s':''}`}
              </Button>
            </div>
          </div>

        </div>
      )}

      {/* ── Step 2: Result ── */}
      {step === 2 && (
        <div>
          {error ? (
            <div style={{
              background: error === 'NETWORK_ERROR' ? '#fff8f0' : '#fef2f2',
              border: `1.5px solid ${error === 'NETWORK_ERROR' ? '#fbbf24' : '#fca5a5'}`,
              borderRadius: 14, padding: '28px 32px', marginBottom: 20,
              display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12,
            }}>
              <div style={{ fontSize: 40 }}>
                {error === 'NETWORK_ERROR' ? '🔌' : '❌'}
              </div>
              <div style={{ fontSize: 18, fontWeight: 800, color: error === 'NETWORK_ERROR' ? '#b45309' : '#b91c1c' }}>
                {error === 'NETWORK_ERROR' ? 'Cannot Reach the Server' : 'Import Failed'}
              </div>

              {error === 'NETWORK_ERROR' ? (
                <div style={{ maxWidth: 480 }}>
                  <p style={{ fontSize: 14, color: '#78350f', marginBottom: 16 }}>
                    The frontend could not connect to the backend API.<br />
                    Please check the following:
                  </p>
                  <div style={{
                    background: '#fffbeb', borderRadius: 10, padding: '14px 20px',
                    border: '1px solid #fde68a', textAlign: 'left', fontSize: 13, color: '#78350f',
                  }}>
                    <div style={{ marginBottom: 8 }}>
                      <strong>1.</strong> Make sure the <strong>NestJS backend is running</strong>:
                      <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, marginLeft: 6, fontFamily: 'monospace' }}>
                        npm run start:dev
                      </code>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>2.</strong> Backend must be on port&nbsp;
                      <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>3001</code>
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <strong>3.</strong> Check your <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>.env</code> /&nbsp;
                      <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>api.js</code> — baseURL should be&nbsp;
                      <code style={{ background: '#fef3c7', padding: '1px 6px', borderRadius: 4, fontFamily: 'monospace' }}>{process.env.REACT_APP_API_URL || window.location.origin}</code>
                    </div>
                    <div>
                      <strong>4.</strong> Check browser console (F12) for CORS or network errors
                    </div>
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: 14, color: '#7f1d1d', maxWidth: 460 }}>
                  {error}
                </div>
              )}

              <div style={{ display: 'flex', gap: 12, marginTop: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  icon={<UploadOutlined />}
                  onClick={() => { setStep(1); setError(''); }}
                  style={{ borderColor: '#f59e0b', color: '#b45309' }}
                >
                  Try Import Again
                </Button>
                <Button
                  onClick={() => { setStep(0); setFile(null); setPreviewRows([]); setError(''); }}
                >
                  ← Choose Different File
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Summary cards */}
              {/* ── Summary hero ── */}
              <div style={{
                background:'#fff', border:'1px solid #dce6f0', borderRadius:16,
                padding:'24px', marginBottom:20, textAlign:'center',
              }}>
                <div style={{fontSize:36,marginBottom:8}}>
                  {(result?.failed??0)===0 ? '✅' : (result?.imported??0)===0 ? '❌' : '⚠️'}
                </div>
                <div style={{fontSize:18,fontWeight:800,color:'#1a2d4a',marginBottom:4}}>
                  {(result?.failed??0)===0
                    ? 'All employees imported successfully!'
                    : `${result?.imported??0} imported, ${result?.failed??0} failed`}
                </div>
                <div style={{fontSize:12,color:'#7a96b0',marginBottom:20}}>
                  {result?.totalRows??0} total rows processed
                </div>
                <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
                  {[
                    {label:'Total Rows', value:result?.totalRows??0, color:'#1E6FBF', bg:'#eff6ff'},
                    {label:'✓ Imported',  value:result?.imported??0,  color:'#15803d', bg:'#f0fdf4'},
                    {label:'✗ Failed',    value:result?.failed??0,    color:'#b91c1c', bg:'#fef2f2'},
                    {label:'Errors',      value:result?.errors?.length??0, color:'#b45309', bg:'#fffbeb'},
                  ].map(s=>(
                    <div key={s.label} style={{
                      padding:'14px 22px', borderRadius:12, minWidth:100,
                      background:s.bg, border:`1px solid ${s.color}33`,
                    }}>
                      <div style={{fontSize:28,fontWeight:800,color:s.color,lineHeight:1}}>{s.value}</div>
                      <div style={{fontSize:11,fontWeight:700,color:'#7a96b0',
                        textTransform:'uppercase',letterSpacing:'0.6px',marginTop:6}}>{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Error detail table */}
              {(result?.errors?.length ?? 0) > 0 && (
                <Card
                  title={
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                      <span style={{ color: '#b91c1c', fontWeight: 600 }}>
                        <WarningOutlined style={{ marginRight: 8 }} />
                        Row Errors ({result?.errors?.length ?? 0}) — {result?.imported??0} succeeded, {result?.failed??0} failed
                      </span>
                      {result?.errorFile && (
                        <Button
                          size="small"
                          icon={<DownloadOutlined />}
                          onClick={() => downloadErrorFile(result.errorFile, 'Import_Errors.xlsx')}
                          style={{ color:'#b91c1c', borderColor:'#fca5a5', background:'#fef2f2', fontWeight:600 }}
                        >
                          Download Error Report (.xlsx)
                        </Button>
                      )}
                    </div>
                  }
                  style={{ borderRadius: 12, border: '1.5px solid #ffd0d0', marginBottom: 20 }}
                  bodyStyle={{ padding: 0 }}
                >
                  <Table
                    dataSource={result.errors}
                    columns={errorColumns}
                    rowKey="row"
                    pagination={{ pageSize: 10, showTotal: t => `${t} errors` }}
                    size="small"
                  />
                </Card>
              )}

              {result?.imported > 0 && (
                <Alert
                  message={`${result.imported} employees imported successfully!`}
                  type="success"
                  showIcon
                  icon={<CheckCircleOutlined />}
                  style={{ marginBottom: 20, borderRadius: 10 }}
                />
              )}
            </>
          )}

          <div style={{ display: 'flex', gap: 12 }}>
            <Button onClick={() => { setStep(0); setFile(null); setResult(null); setError(''); }}>
              Upload Another File
            </Button>
            <Button
              type="primary"
              style={{ background: '#1B2D6B' }}
              onClick={() => navigate('/employees')}
            >
              Go to Employees
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}